/**
 * AdminJotAuditDashboardEmbedded
 *
 * Variant of AdminJotAuditDashboardPage for embedding under another admin
 * surface (e.g. the org detail Configuration sub-tab). Takes organizationId as
 * a prop, manages its inner tab as local state (no URL coupling), and drops
 * the page header / back button / outer JotAuditPageLayout — the host surface
 * supplies that chrome.
 *
 * Drill-down links (note, provider, rule analytics) still navigate to the
 * standalone /admin-v2/jotaudit/:organizationId/... routes.
 */

import React, { useCallback, useState } from 'react';

import { Box, Paper, Tab, Tabs } from '@mui/material';

import { EncounterAuditsList } from './components/encounters/EncounterAuditsList';
import { ProvidersList } from './components/providers/ProvidersList';
import { RulesTabContent } from './components/rules/RulesTabContent';
import { TAB_CONFIG } from './constants';
import type { JotAuditTabType } from './types';

const DEFAULT_TAB: JotAuditTabType = 'encounters';

interface AdminJotAuditDashboardEmbeddedProps {
  organizationId: string;
}

export function AdminJotAuditDashboardEmbedded({
  organizationId,
}: AdminJotAuditDashboardEmbeddedProps) {
  const [currentTab, setCurrentTab] = useState<JotAuditTabType>(DEFAULT_TAB);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: JotAuditTabType) => {
      setCurrentTab(newValue);
    },
    [],
  );

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              py: 2,
              fontSize: '0.9375rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab value={TAB_CONFIG.encounters.value} label={TAB_CONFIG.encounters.label} />
          <Tab value={TAB_CONFIG.rules.value} label={TAB_CONFIG.rules.label} />
          <Tab value={TAB_CONFIG.providers.value} label={TAB_CONFIG.providers.label} />
        </Tabs>
      </Paper>

      {currentTab === 'encounters' && (
        <EncounterAuditsList organizationId={organizationId} />
      )}
      {currentTab === 'rules' && <RulesTabContent organizationId={organizationId} />}
      {currentTab === 'providers' && <ProvidersList organizationId={organizationId} />}
    </Box>
  );
}
